import { persistedState } from 'svelte-persisted-state';
import { v4 as uuidv4 } from 'uuid';

import type { Nullable, RoastQueue, RoastQueueItem } from '$lib/types/RoastLog';
import { RoastQueueStatus } from '$lib/types/RoastLog';

export class RoastingQueue {
  currentIndex: number = $state(0);
  currentItem: Nullable<RoastQueueItem> = $state(null);
  queueItems = persistedState<RoastQueue>('roastQueue', []);

  constructor() {
    this.currentIndex = this.findActiveIndex();
  }

  get items() {
    return this.queueItems.current;
  }

  set items(qItems: RoastQueue) {
    this.queueItems.current = qItems;
    this.currentIndex = this.findActiveIndex();
  }

  get length(): number {
    return this.queueItems.current.length;
  }

  get unroastedLength() {
    return this.items.reduce((count: number, qItem) => {
      if (
        qItem.status === RoastQueueStatus.Queued ||
        qItem.status === RoastQueueStatus.InProgress
      ) {
        return count + 1;
      } else {
        return count;
      }
    }, 0);
  }

  append(item: RoastQueueItem) {
    this.items = [...this.queueItems.current, item];
  }

  at(index: number): RoastQueueItem {
    return this.queueItems.current[index];
  }

  reset() {
    this.queueItems.reset();
    this.currentIndex = 0;
    this.currentItem = null;
  }

  deleteItemById(itemId: string) {
    this.queueItems.current = this.queueItems.current.filter((qItem) => qItem.id !== itemId);
    this.currentIndex = this.findActiveIndex();
  }

  duplicateItemById(itemId: string) {
    const qIndex = this.getItemIndexById(itemId);
    if (qIndex === null) {
      return;
    }
    const newItem: RoastQueueItem = Object.assign(
      { ...this.queueItems.current[qIndex] },
      {
        id: uuidv4(),
        roastId: undefined,
        status: RoastQueueStatus.Queued,
        notes: undefined,
        endingMass: undefined,
        roastLevel: undefined
      }
    );

    this.append(newItem);
  }

  findItemById(itemId: string) {
    return this.queueItems.current.find((qItem) => qItem.id === itemId);
  }

  setCurrentStatus(status: RoastQueueStatus) {
    this.queueItems.current[this.currentIndex].status = status;
    this.currentIndex = this.findActiveIndex();
  }

  setStatusById(itemId: string, status: RoastQueueStatus) {
    const qIndex = this.getItemIndexById(itemId);
    if (qIndex === null) {
      return;
    }

    this.queueItems.current[qIndex].status = status;
    this.currentIndex = this.findActiveIndex();
  }

  getItemIndexById(itemId: string): Nullable<number> {
    const qIndex = this.queueItems.current.findIndex((item) => item.id === itemId);
    if (qIndex !== -1) {
      return qIndex;
    }
    return null;
  }

  updateItemAt = (index: number, updates: Partial<RoastQueueItem>): RoastQueueItem => {
    const item = this.items[index];
    const newItem = { ...item, ...updates };
    this.items[index] = newItem;
    this.currentIndex = this.findActiveIndex();
    return newItem;
  };

  /**
   * Finds the first item in the queue that is not queued or in progress. This is the item that
   * all operations should be performed on.
   * @returns The index of the first item that is not queued or in progress.
   */
  private findActiveIndex(): number {
    const qIndex = this.queueItems.current.findIndex(
      (qItem) =>
        qItem.status === RoastQueueStatus.Queued || qItem.status === RoastQueueStatus.InProgress
    );

    this.currentItem = qIndex === -1 ? null : this.queueItems.current[qIndex];

    return qIndex;
  }
}
